import { useAccount } from "@qw/auth";
import { type GetConversationsInput, useInboxAgentSearchLazyQuery } from "@qw/graphql";
import { KnownInboxLabel } from "@qw/qw-common";
import { usePagination } from "@qw/ui";
import {
  capture,
  PostHogEvent,
  SearchFilterAction,
  SearchFilterSurface,
} from "@third-plane/posthog-integration-interface";
import isEqual from "lodash/isEqual";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router";
import { useDebounceValue, useLocalStorage } from "usehooks-ts";
import { type ParsedSearchQuery, searchTextLength } from "@/search/search-operators";
import { type InboxQueryStatesContextValue, useInboxQueryStates } from "./inbox-query-states-provider";
import { scopedAgencyId, scopedAgentId, scopedInsuredId, useInboxScope } from "./inbox-scope";

// A committed search only reaches the server once the typing has settled, because it is a scan.
const SEARCH_DEBOUNCE_MS = 500;

/**
 * Shortest term the agent probe will ask the server about.
 *
 * Mirrors `MINIMUM_AGENT_SEARCH_TERM_LENGTH` in `agent-search.query.ts`, which is API-side and so
 * cannot be imported here, and `MINIMUM_SUGGESTION_TERM_LENGTH` in `qwib-search.tsx`, which is the
 * dropdown's copy of the same floor. Three now, and a committed search can be shorter than it —
 * `hasSearch` admits a single character — so without this the probe pays a round trip the server
 * answers with `[]` before it reaches Postgres.
 */
const MINIMUM_AGENT_PROBE_TERM_LENGTH = 3;

interface SearchTracking {
  startedAt: number;
  query: ParsedSearchQuery;
}

export interface InboxFilterContextValue {
  filters: GetConversationsInput;
  /**
   * Whether the list on screen is a search result rather than a mailbox. Not the term any more:
   * `subject:renewal` carries no unscoped term at all, so reading `filters.search` stopped
   * answering the question the moment operators arrived.
   */
  hasSearch: boolean;
  labels: string[];
  notLabels: string[];
  priorityLabels: string[];
  search: {
    onCompleted: (resultCount: number) => void;
    onError: () => void;
    searchTerm: string;
    setSearchTerm: (searchTerm: string) => void;
  };
  unlinked: boolean;
  setLabels: InboxQueryStatesContextValue["setLabels"];
  setNotLabels: InboxQueryStatesContextValue["setNotLabels"];
  setUnlinked: InboxQueryStatesContextValue["setUnlinked"];
  setPriorityLabels: React.Dispatch<React.SetStateAction<string[]>>;
}

export const InboxFilterProvider = ({ children }: { children: React.ReactNode }) => {
  const [priorityLabels, setPriorityLabels] = useLocalStorage<string[]>("priorityLabels", [KnownInboxLabel.STARRED]);
  const {
    searchTerm,
    setSearchTerm,
    subjectSearch,
    senderSearch,
    unlinked,
    setUnlinked,
    labels,
    setLabels,
    notLabels,
    setNotLabels,
    page,
  } = useInboxQueryStates();

  /**
   * The committed search as the three dimensions the server takes, debounced as one value.
   *
   * One object rather than three pieces of state because they move together: a commit writes
   * `?term=`, `?subject=` and `?from=` in a single navigation, so debouncing only the term would
   * fire a query for the operators alone — a *wider* search than the broker asked for — and then
   * the real one 500ms later.
   */
  const committedQuery = useMemo<ParsedSearchQuery>(
    () => ({
      search: searchTerm,
      // `""` is how an unset param reads; the server wants the field absent.
      subjectSearch: subjectSearch || undefined,
      senderSearch: senderSearch || undefined,
    }),
    [searchTerm, subjectSearch, senderSearch]
  );
  const [query] = useDebounceValue(committedQuery, SEARCH_DEBOUNCE_MS);
  const hasSearch = searchTextLength(query) > 0;

  const { userInbox } = useAccount();
  const inboxIds = useMemo(() => (userInbox ? [userInbox.id] : []), [userInbox]);
  const scope = useInboxScope();
  const { pathname, search: locationSearch } = useLocation();
  const { rowsPerPage } = usePagination();
  const hasExplicitLabels = useMemo(() => new URLSearchParams(locationSearch).has("labels"), [locationSearch]);
  const searchTermMountedRef = useRef(false);
  const searchTrackingRef = useRef<SearchTracking | null>(null);

  useEffect(() => {
    if (!searchTermMountedRef.current) {
      searchTermMountedRef.current = true;
      return;
    }

    searchTrackingRef.current = { startedAt: Date.now(), query: committedQuery };
  }, [committedQuery]);

  /**
   * The committed search's own key, plus one per operator narrowing it.
   *
   * `["conversations"]` stays first and stays unconditional — four weeks of committed-search counts
   * key on it, and a scoped search is still a committed search. The operator rides alongside so
   * `subject:` and `from:` are countable against the Unlinked-zero baseline without a new event
   * name: the taxonomy lives in a published package, so a rename is not a local change.
   */
  const searchFilterKeys = useMemo(() => {
    const keys = ["conversations"];

    if (query.subjectSearch) {
      keys.push("subject");
    }

    if (query.senderSearch) {
      keys.push("from");
    }

    return keys;
  }, [query]);

  // Every id comes off the one scope inside the memo rather than beside it, so the scope is the
  // single dependency: a `scope` that has not changed cannot have changed any of them.
  const filters = useMemo(() => {
    const insuredId = scopedInsuredId(scope);

    return {
      inboxIds,
      insuredId,
      agentId: scopedAgentId(scope),
      // Sent whole rather than expanded into the agency's agents: who counts as in the agency —
      // deleted, deactivated, test accounts — is a policy that belongs next to the same
      // `PrismaService`, not in a client-held roster that goes stale the moment an agent joins.
      agencyId: scopedAgencyId(scope),
      // Any scope drops the default folder for the reason it was picked: the broker asked for this
      // insured's, this agent's or this agency's mail, and `[INBOX]` would hide the half of it they
      // sent. An explicit `?labels=` is the broker naming a folder inside the scope, so it survives.
      labels: hasSearch || (scope && !hasExplicitLabels && isEqual(labels, [KnownInboxLabel.INBOX])) ? [] : labels,
      notLabels: labels.includes(KnownInboxLabel.TRASH)
        ? notLabels.filter((label) => label !== KnownInboxLabel.TRASH)
        : notLabels,
      priorityLabels,
      limit: Number(rowsPerPage) || 50,
      offset: (page - 1) * Number(rowsPerPage) || 0,
      search: query.search,
      subjectSearch: query.subjectSearch,
      senderSearch: query.senderSearch,
      unlinked,
      // Deal-scoped, not mailbox-scoped: also surface conversations linked to marketing plans the
      // user collaborates on. Only when the list is scoped to a specific insured (the book-filtered
      // view) — not in the general inbox, where these foreign-inbox rows would clutter the main
      // triage queue. The server also respects insuredId, so scoping here composes with its filter.
      includeCollaboratedDeals: Boolean(insuredId),
    };
  }, [
    inboxIds,
    scope,
    hasExplicitLabels,
    labels,
    notLabels,
    priorityLabels,
    page,
    rowsPerPage,
    query,
    hasSearch,
    unlinked,
  ]);

  const [runAgentSearch] = useInboxAgentSearchLazyQuery();

  /**
   * Whether the unscoped half of a committed term names an agent this mailbox has corresponded
   * with — the one thing we are allowed to keep about what a broker typed.
   *
   * Only the unscoped half, because `from:` is already its own `filterKeys` entry: counting a
   * sender scope here too would put one behaviour under two keys and make the agent-name rate
   * unreadable. A term of only operators asks nothing of this query and is not sent.
   *
   * Deliberately asks for the default page rather than one row. Once the dropdown renders agents
   * it will have run this exact operation as-you-type, and matching its variables is what makes
   * this a cache hit rather than a second request per committed search — but only above
   * {@link MINIMUM_AGENT_PROBE_TERM_LENGTH}, since the dropdown skips shorter terms too. Nothing
   * would ever cache those, which is why the floor is checked here rather than left to the server.
   */
  const probeAgentMatch = useCallback(
    async (term: string) => {
      if (term.trim().length < MINIMUM_AGENT_PROBE_TERM_LENGTH) {
        return false;
      }

      // `useLazyQuery` resolves rather than rejects on failure, handing back an undefined `data`.
      // A failed probe reads as "no match", which is the same direction as the one-sidedness this
      // flag already carries — and losing the event outright would dent the committed-search
      // baseline that four weeks of `["conversations"]` counts are measured against.
      const { data } = await runAgentSearch({ variables: { input: { inboxIds, term } } });

      return Boolean(data?.inboxAgentSearch.length);
    },
    [runAgentSearch, inboxIds]
  );

  /**
   * Async, but fire-and-forget by contract: `useConversations` hands this to Apollo's
   * `onCompleted`, which has no result to wait for, so the context type stays `void`.
   */
  const onSearchCompleted = useCallback(
    async (resultCount: number) => {
      const searchTracking = searchTrackingRef.current;

      // Compared by value rather than by reference: the tracked query is the undebounced one, and
      // it becomes *this* query only once the debounce settles onto it.
      if (!searchTracking || !hasSearch || !isEqual(searchTracking.query, query)) {
        return;
      }

      // Both spent before the probe below. The guard, so a second consumer observing the same
      // results finds it gone rather than emitting a duplicate while this one is in flight; the
      // clock, because the probe runs after the results the broker is already reading and timing
      // it as part of the search would overstate every committed search by a round trip.
      searchTrackingRef.current = null;
      const durationMs = Date.now() - searchTracking.startedAt;
      const matchedAgent = await probeAgentMatch(query.search);

      capture(PostHogEvent.SearchFilterUsed, {
        routeName: pathname,
        surface: SearchFilterSurface.Global,
        action: SearchFilterAction.Search,
        // A key on the existing array rather than a `matchedAgent` property of its own: the
        // taxonomy is a published package, so `SearchFilterUsedProperties` cannot grow a field
        // from here — the same constraint that put `subject` and `from` in this array. Appended
        // last so `["conversations"]` stays first and the operators keep their order, both of
        // which are load-bearing for counts that already exist.
        filterKeys: matchedAgent ? [...searchFilterKeys, "matched_agent"] : searchFilterKeys,
        resultCount,
        durationMs,
      });
    },
    [hasSearch, probeAgentMatch, query, searchFilterKeys, pathname]
  );

  const onSearchError = useCallback(() => {
    if (isEqual(searchTrackingRef.current?.query, query)) {
      searchTrackingRef.current = null;
    }
  }, [query]);

  const value: InboxFilterContextValue = useMemo(
    () => ({
      filters,
      hasSearch,
      labels,
      notLabels,
      priorityLabels,
      search: { onCompleted: onSearchCompleted, onError: onSearchError, searchTerm, setSearchTerm },
      unlinked,
      setLabels,
      setNotLabels,
      setPriorityLabels,
      setUnlinked,
    }),
    [
      filters,
      hasSearch,
      labels,
      notLabels,
      priorityLabels,
      onSearchCompleted,
      onSearchError,
      searchTerm,
      unlinked,
      setLabels,
      setNotLabels,
      setPriorityLabels,
      setSearchTerm,
      setUnlinked,
    ]
  );

  return <InboxFilterContext.Provider value={value}>{children}</InboxFilterContext.Provider>;
};

export const InboxFilterContext = createContext<InboxFilterContextValue | null>(null);

export const useInboxFilter = () => {
  const context = useContext(InboxFilterContext);

  if (!context) {
    throw new Error("useInboxFilter must be used within a InboxFilterProvider");
  }

  return context;
};
